/**
 * Shared editor used by Quotations and Invoices.
 * Calls /admin/quotations or /admin/invoices according to `kind`.
 */
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Trash2, FileDown, Share2, Eye, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { PageHeader, AdminBody, Card, Button, Modal, Table, StatusBadge } from "./Bits";

export default function BillingPage({ kind = "quotation" }) {
  const isInvoice = kind === "invoice";
  const endpoint = isInvoice ? "/admin/invoices" : "/admin/quotations";
  const numberField = isInvoice ? "invoice_number" : "quote_number";

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft(isInvoice));
  const [editing, setEditing] = useState(null);

  const load = () => api.get(endpoint).then(r => setRows(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const totals = computeTotals(draft);

  const save = async () => {
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, draft);
      else await api.post(endpoint, draft);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const openNew = () => { setEditing(null); setDraft(emptyDraft(isInvoice)); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setDraft({ ...emptyDraft(isInvoice), ...r }); setOpen(true); };
  const remove = async (r) => { if (window.confirm("Delete?")) { await api.delete(`${endpoint}/${r.id}`); load(); } };

  const openPDF = async (r) => {
    try {
      const res = await api.get(`${endpoint}/${r.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const fname = (r[numberField] || (isInvoice ? "invoice" : "quotation")) + ".pdf";
      // open in new tab
      const w = window.open(url, "_blank");
      // also offer download
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (!w) toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not load PDF");
    }
  };

  const convertToInvoice = async (r) => {
    const res = await api.post(`/admin/quotations/${r.id}/convert`);
    toast.success(`Converted to ${res.data.invoice_number}`);
    load();
  };

  const setLine = (i, patch) => setDraft(d => ({ ...d, lines: d.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const addLine = () => setDraft(d => ({ ...d, lines: [...d.lines, { name: "", qty: 1, rate: 0, amount: null }] }));
  const removeLine = (i) => setDraft(d => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }));

  return (
    <>
      <PageHeader
        title={isInvoice ? "GST Invoices" : "Quotations"}
        subtitle={isInvoice ? "Create, edit and download GST-compliant tax invoices." : "Build proposals and convert into invoices in one click."}
        action={<Button onClick={openNew} data-testid={`${kind}-add`}><Plus size={14} /> New {isInvoice ? "Invoice" : "Quote"}</Button>}
      />
      <AdminBody>
        <Card>
          <Table
            columns={[
              { title: "No.", render: r => <div className="serif text-base text-gold">{r[numberField]}</div> },
              { title: "Customer", render: r => <><div className="serif text-base">{r.customer_name}</div><div className="text-navy-900/50 text-xs">{r.customer_phone}</div></> },
              { title: "Event", render: r => <>{r.event_type || "—"}<div className="text-navy-900/50 text-xs">{r.event_date}</div></> },
              { title: "Total", render: r => <span className="serif text-lg">₹{(r.grand_total || 0).toLocaleString("en-IN")}</span> },
              { title: "Status", render: r => <StatusBadge status={isInvoice ? r.payment_status : r.status} /> },
              { title: "Date", render: r => <span className="text-xs">{new Date(r.created_at).toLocaleDateString()}</span> },
            ]}
            rows={rows}
            testIdPrefix={kind}
            actions={(r) => (
              <div className="flex gap-2 justify-end">
                <button onClick={() => openPDF(r)} className="text-navy-900 hover:text-gold p-2" title="PDF" data-testid={`${kind}-pdf-${r.id}`}><FileDown size={14} /></button>
                <button onClick={() => openEdit(r)} className="text-navy-900 hover:text-gold p-2" title="Edit"><Eye size={14} /></button>
                {!isInvoice && r.status !== "converted" && (
                  <button onClick={() => convertToInvoice(r)} className="text-navy-900 hover:text-gold p-2" title="Convert" data-testid={`${kind}-convert-${r.id}`}><ArrowRight size={14} /></button>
                )}
                <button onClick={() => remove(r)} className="text-maroon hover:opacity-70 p-2"><Trash2 size={14} /></button>
              </div>
            )}
          />
        </Card>

        <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit ${editing[numberField]}` : `New ${isInvoice ? "Invoice" : "Quotation"}`} maxWidth="max-w-5xl">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Customer Name" v={draft.customer_name} on={v => setDraft({ ...draft, customer_name: v })} testId="bill-customer-name" />
            <Field label="Phone" v={draft.customer_phone} on={v => setDraft({ ...draft, customer_phone: v })} />
            <Field label="Email" v={draft.customer_email} on={v => setDraft({ ...draft, customer_email: v })} />
            <Field label="Event Type" v={draft.event_type} on={v => setDraft({ ...draft, event_type: v })} />
            <Field label="Event Date" v={draft.event_date} type="date" on={v => setDraft({ ...draft, event_date: v })} />
            <Field label="Guests" v={draft.guests} type="number" on={v => setDraft({ ...draft, guests: parseInt(v) || 0 })} />
            <Field label="Venue" v={draft.venue} on={v => setDraft({ ...draft, venue: v })} />
            <Field label="Place of Supply" v={draft.place_of_supply} on={v => setDraft({ ...draft, place_of_supply: v })} />
            <div className="sm:col-span-2"><label className="label">Customer Address</label><textarea className="input min-h-[60px]" value={draft.customer_address || ""} onChange={e => setDraft({ ...draft, customer_address: e.target.value })} /></div>
          </div>

          <div className="mt-7">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">Line Items</div>
              <Button variant="outline" onClick={addLine} className="!py-1 !px-3 !text-[10px]" data-testid="bill-add-line"><Plus size={12} /> Line</Button>
            </div>
            <div className="overflow-x-auto border border-navy-900/10">
              <table className="w-full text-sm">
                <thead className="bg-navy-900 text-gold">
                  <tr>
                    <th className="text-left p-3 text-[10px] tracking-wider uppercase w-1/3">Item</th>
                    <th className="text-left p-3 text-[10px] tracking-wider uppercase">Description</th>
                    <th className="text-right p-3 text-[10px] tracking-wider uppercase w-20">Qty</th>
                    <th className="text-right p-3 text-[10px] tracking-wider uppercase w-28">Rate</th>
                    <th className="text-right p-3 text-[10px] tracking-wider uppercase w-28">Amount</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {draft.lines.map((l, i) => {
                    const computed = (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0);
                    return (
                      <tr key={i} className="border-t border-navy-900/8" data-testid={`bill-line-${i}`}>
                        <td className="p-1"><input className="input !py-2 !text-sm" value={l.name || ""} onChange={e => setLine(i, { name: e.target.value })} placeholder="e.g. Gold Package" /></td>
                        <td className="p-1"><input className="input !py-2 !text-sm" value={l.description || ""} onChange={e => setLine(i, { description: e.target.value })} /></td>
                        <td className="p-1"><input type="number" className="input !py-2 !text-sm text-right" value={l.qty ?? 0} onChange={e => setLine(i, { qty: parseFloat(e.target.value) || 0 })} /></td>
                        <td className="p-1"><input type="number" className="input !py-2 !text-sm text-right" value={l.rate ?? 0} onChange={e => setLine(i, { rate: parseFloat(e.target.value) || 0 })} /></td>
                        <td className="p-1"><input type="number" className="input !py-2 !text-sm text-right" placeholder={computed.toFixed(2)} value={l.amount ?? ""} onChange={e => setLine(i, { amount: e.target.value === "" ? null : parseFloat(e.target.value) })} /></td>
                        <td className="p-1 text-center"><button onClick={() => removeLine(i)} className="text-maroon hover:opacity-70" data-testid={`bill-line-del-${i}`}><Trash2 size={14} /></button></td>
                      </tr>
                    );
                  })}
                  {draft.lines.length === 0 && <tr><td colSpan={6} className="p-5 text-center text-navy-900/50">No line items — add one.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div className="space-y-3">
              <Field label="Notes" v={draft.notes} on={v => setDraft({ ...draft, notes: v })} textarea />
              {isInvoice && (
                <>
                  <Field label="Invoice Date" v={draft.invoice_date} type="date" on={v => setDraft({ ...draft, invoice_date: v })} />
                  <Field label="Due Date" v={draft.due_date} type="date" on={v => setDraft({ ...draft, due_date: v })} />
                  <div>
                    <label className="label">Payment Status</label>
                    <select className="input" value={draft.payment_status || "unpaid"} onChange={e => setDraft({ ...draft, payment_status: e.target.value })}>
                      {["unpaid", "partial", "paid"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </>
              )}
              {!isInvoice && (
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={draft.status || "draft"} onChange={e => setDraft({ ...draft, status: e.target.value })}>
                    {["draft", "sent", "accepted", "rejected"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="border border-gold/30 p-5 bg-ivory-200">
              <div className="label-eyebrow mb-3">Totals</div>
              <Row label="Subtotal" value={`₹${totals.subtotal.toFixed(2)}`} />
              <NumRow label="Discount" v={draft.discount} on={v => setDraft({ ...draft, discount: v })} />
              <Row label="Taxable" value={`₹${totals.taxable.toFixed(2)}`} />
              <NumRow label="CGST %" v={draft.cgst_pct} on={v => setDraft({ ...draft, cgst_pct: v })} />
              <NumRow label="SGST %" v={draft.sgst_pct} on={v => setDraft({ ...draft, sgst_pct: v })} />
              <NumRow label="IGST %" v={draft.igst_pct} on={v => setDraft({ ...draft, igst_pct: v })} />
              <NumRow label="Advance" v={draft.advance} on={v => setDraft({ ...draft, advance: v })} />
              <div className="border-t border-gold/40 mt-2 pt-2 flex justify-between serif text-xl"><span>Grand Total</span><span className="text-gold">₹{totals.grand.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm mt-1"><span>Balance Due</span><span>₹{totals.balance.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-7">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="bill-save">Save</Button>
            {editing && <Button variant="outline" onClick={() => openPDF(editing)} data-testid="bill-pdf"><FileDown size={14} /> PDF</Button>}
          </div>
        </Modal>
      </AdminBody>
    </>
  );
}

function emptyDraft(isInvoice) {
  return {
    customer_name: "", customer_phone: "", customer_email: "", customer_address: "",
    event_type: "", event_date: "", guests: 0, venue: "", notes: "",
    lines: [{ name: "", qty: 1, rate: 0, amount: null }],
    discount: 0, cgst_pct: isInvoice ? 9 : 0, sgst_pct: isInvoice ? 9 : 0, igst_pct: 0, advance: 0,
    status: "draft", payment_status: "unpaid", place_of_supply: "Gujarat",
    invoice_date: new Date().toISOString().slice(0, 10),
  };
}

function computeTotals(d) {
  let subtotal = 0;
  for (const l of d.lines || []) {
    const a = (l.amount != null && l.amount !== "") ? parseFloat(l.amount) : (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0);
    subtotal += a;
  }
  const discount = parseFloat(d.discount) || 0;
  const taxable = Math.max(subtotal - discount, 0);
  const cgst = taxable * (parseFloat(d.cgst_pct) || 0) / 100;
  const sgst = taxable * (parseFloat(d.sgst_pct) || 0) / 100;
  const igst = taxable * (parseFloat(d.igst_pct) || 0) / 100;
  const grand = taxable + cgst + sgst + igst;
  const advance = parseFloat(d.advance) || 0;
  const balance = grand - advance;
  return { subtotal, taxable, cgst, sgst, igst, grand, balance };
}

function Field({ label, v, on, type = "text", textarea, testId }) {
  return (
    <div>
      <label className="label">{label}</label>
      {textarea ? <textarea className="input min-h-[70px]" value={v || ""} onChange={e => on(e.target.value)} data-testid={testId} />
        : <input type={type} className="input" value={v || ""} onChange={e => on(e.target.value)} data-testid={testId} />}
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between py-1 text-sm"><span className="text-navy-900/50">{label}</span><span>{value}</span></div>;
}

function NumRow({ label, v, on }) {
  return (
    <div className="flex justify-between items-center py-1 text-sm">
      <span className="text-navy-900/50">{label}</span>
      <input type="number" className="input !py-1 !w-28 text-right text-sm" value={v ?? 0} onChange={e => on(parseFloat(e.target.value) || 0)} />
    </div>
  );
}
